import { supabase } from "@/lib/supabase-client";
import { UserType } from "@/types/db/users";
import { EmergencyContactType } from "@/types/db/user_emergency_contacts";
import { FileMetadata } from "@/types/files";
import { FromDB } from "@/types";
import { clearAvatarCache } from "@/components/Avatar";
import { AssignableUser, GetAssignableUsersParams } from "@/types/db/assignable_user";

export interface PasswordRequirements {
  min_length: number;
  require_uppercase: boolean;
  require_lowercase: boolean;
  require_number: boolean;
  require_special: boolean;
}

export interface UserAccountInfo {
  created_at: string;
  last_sign_in_at: string | null;
  has_signed_in: boolean;
  email_confirmed_at: string | null;
  password_last_updated: string | null;
  password_expires_at: string | null;
  needs_password_update: boolean;
  password_requirements?: PasswordRequirements;
}

export const userService = {
  async getUsers(filters?: { userIds?: string[] }): Promise<FromDB<UserType>[]> {
    let query = supabase
      .from("users")
      .select(`id, first_name, middle_name, last_name, preferred_name, email_address, status, meta_data, created_at`)
      .order("created_at", { ascending: false })
      .limit(9000);

    // Apply filters if provided
    if (filters?.userIds && filters.userIds.length > 0) {
      query = query.in("id", filters.userIds);
    }

    const { data, error } = await query;
    if (error) throw new Error(error.message);

    // Extract markets from meta_data for table display
    return (data as FromDB<UserType>[]).map((user) => ({
      ...user,
      markets: (user.meta_data as any)?.markets || [],
    }));
  },

  async getUsersForAssignment(filters?: { userIds?: string[] }): Promise<UserType[]> {
    // Fetch basic user data
    let query = supabase
      .from("users")
      .select(`id, first_name, middle_name, last_name, preferred_name, email_address, status, meta_data`)
      .limit(9000);

    // Apply filters if provided
    if (filters?.userIds && filters.userIds.length > 0) {
      query = query.in("id", filters.userIds);
    }

    const { data: users, error } = await query;
    if (error) throw new Error(error.message);
    if (!users || users.length === 0) return [];

    const userIds = users.map((u) => u.id);

    // Batch fetch all link data for efficiency
    const [positionsLinkData, abilitiesLinkData, restrictionsLinkData] = await Promise.all([
      supabase.schema("link").from("user_positions").select("id, position_id, meta_data").in("id", userIds),
      supabase.schema("link").from("user_abilities").select("id, ability_id, expires").in("id", userIds),
      supabase.schema("link").from("user_restrictions").select("id, restriction_id, expires").in("id", userIds),
    ]);

    // Collect all unique IDs for lists tables
    const positionIds = new Set(positionsLinkData.data?.map((p) => p.position_id) || []);
    const abilityIds = new Set(abilitiesLinkData.data?.map((a) => a.ability_id) || []);
    const restrictionIds = new Set(restrictionsLinkData.data?.map((r) => r.restriction_id) || []);

    // Batch fetch from lists tables
    const [positionsData, abilitiesData, restrictionsData] = await Promise.all([
      positionIds.size > 0
        ? supabase
            .schema("lists")
            .from("positions")
            .select("id, name, description, billing_rate")
            .in("id", Array.from(positionIds))
        : Promise.resolve({ data: [] }),
      abilityIds.size > 0
        ? supabase.schema("lists").from("abilities").select("id, name, description").in("id", Array.from(abilityIds))
        : Promise.resolve({ data: [] }),
      restrictionIds.size > 0
        ? supabase
            .schema("lists")
            .from("restrictions")
            .select("id, name, description")
            .in("id", Array.from(restrictionIds))
        : Promise.resolve({ data: [] }),
    ]);

    // Create lookup maps
    const positionsMap = new Map(positionsData.data?.map((p) => [p.id, p] as const) || []);
    const abilitiesMap = new Map(abilitiesData.data?.map((a) => [a.id, a] as const) || []);
    const restrictionsMap = new Map(restrictionsData.data?.map((r) => [r.id, r] as const) || []);

    // Map everything back to users
    return users.map((user) => {
      // Get positions for this user
      const userPositionsLinks = positionsLinkData.data?.filter((p) => p.id === user.id) || [];
      const user_positions = userPositionsLinks
        .map((link) => {
          const position = positionsMap.get(link.position_id);
          return position ? { position, meta_data: link.meta_data ?? {} } : null;
        })
        .filter((p) => p !== null) as { position: any; meta_data?: any }[];

      // Get abilities for this user
      const userAbilitiesLinks = abilitiesLinkData.data?.filter((a) => a.id === user.id) || [];
      const user_abilities = userAbilitiesLinks
        .map((link) => {
          const ability = abilitiesMap.get(link.ability_id);
          return ability ? { ability, expires: link.expires || null } : null;
        })
        .filter((a) => a !== null) as { ability: any; expires: string | null }[];

      // Get restrictions for this user
      const userRestrictionsLinks = restrictionsLinkData.data?.filter((r) => r.id === user.id) || [];
      const user_restrictions = userRestrictionsLinks
        .map((link) => {
          const restriction = restrictionsMap.get(link.restriction_id);
          return restriction ? { restriction, expires: link.expires || null } : null;
        })
        .filter((r) => r !== null) as { restriction: any; expires: string | null }[];

      return {
        ...user,
        user_positions,
        user_abilities,
        user_restrictions,
      } as UserType;
    });
  },

  /**
   * Get users eligible for shift position assignment via RPC function.
   * Returns users with eligibility flags, hours data, and enriched abilities/restrictions.
   */
  async getAssignableUsers(params: GetAssignableUsersParams): Promise<AssignableUser[]> {
    const { data, error } = await supabase.rpc("get_assignable_users", {
      p_shift_id: params.shiftId,
      p_position_id: params.positionId || null,
      p_required_ability_ids: params.requiredAbilityIds || [],
      p_required_restriction_ids: params.requiredRestrictionIds || [],
      p_supervisor_required: params.supervisorRequired || false,
      p_week_start_date: params.weekStartDate || null,
    });

    if (error) {
      throw new Error(`Failed to fetch assignable users: ${error.message}`);
    }

    return (data || []) as AssignableUser[];
  },

  async getUserById(id: string): Promise<UserType | null> {
    const { data, error } = await supabase.rpc("get_user", { p_user_id: id });
    if (error) throw Error(error.message);
    return data as UserType;
  },

  async addUser(userData: {
    first_name: string;
    middle_name?: string;
    last_name: string;
    email_address: string;
    preferred_name?: string;
    meta_data?: any;
    tenant_id: string;
  }): Promise<UserType | null> {
    const { data, error } = await supabase.from("users").insert([userData]).select().single();
    if (error) {
      console.error("Error adding User:", error.message);
      throw new Error(error.message);
    }
    return data as UserType;
  },

  async createUserWithAuth(
    userData: {
      first_name: string;
      middle_name?: string;
      last_name: string;
      email_address: string;
      preferred_name?: string;
      password?: string;
      email_confirm?: boolean;
      email_verified?: boolean;
      meta_data?: any;
    },
    accessToken: string,
  ): Promise<{ user: UserType | null; temporaryPassword?: string }> {
    const { data, error } = await supabase.functions.invoke("create-user", {
      body: userData,
      headers: {
        Authorization: `Bearer ${accessToken}`,
      },
    });

    if (error) {
      throw new Error(error.message || "Failed to create user");
    }

    if (!data) {
      throw new Error("No data returned from edge function");
    }

    // Check for error response (edge function returns 200 with success: false for errors)
    if (!data.success) {
      const errorMessage = data.error || data.message || "";
      if (
        data.code === "email_exists" ||
        errorMessage.includes("already been registered") ||
        errorMessage.includes("already registered")
      ) {
        throw new Error("A user with this email address already exists");
      }
      throw new Error(errorMessage || "Failed to create user");
    }

    return {
      user: data.user,
      temporaryPassword: data.temporaryPassword,
    };
  },

  async getUserAccountInfo(userId: string, accessToken: string): Promise<UserAccountInfo> {
    const { data, error } = await supabase.functions.invoke("user-account", {
      body: { user_id: userId, action: "get_account_info" },
      headers: { Authorization: `Bearer ${accessToken}` },
    });

    if (error) throw new Error(error.message || "Failed to fetch account info");
    if (!data?.success) throw new Error(data?.error || "Failed to fetch account info");
    return {
      ...data.account,
      password_requirements: data.password_requirements,
    } as UserAccountInfo;
  },

  async resendInvite(userId: string, accessToken: string): Promise<string> {
    const { data, error } = await supabase.functions.invoke("user-account", {
      body: { user_id: userId, action: "resend_invite" },
      headers: { Authorization: `Bearer ${accessToken}` },
    });

    if (error) throw new Error(error.message || "Failed to resend invite");
    if (!data?.success) throw new Error(data?.error || "Failed to resend invite");
    return data.message;
  },

  async sendPasswordReset(userId: string, accessToken: string): Promise<string> {
    const { data, error } = await supabase.functions.invoke("user-account", {
      body: { user_id: userId, action: "send_password_reset" },
      headers: { Authorization: `Bearer ${accessToken}` },
    });

    if (error) throw new Error(error.message || "Failed to send password reset");
    if (!data?.success) throw new Error(data?.error || "Failed to send password reset");
    return data.message;
  },

  async confirmEmail(userId: string, accessToken: string): Promise<string> {
    const { data, error } = await supabase.functions.invoke("user-account", {
      body: { user_id: userId, action: "confirm_email" },
      headers: { Authorization: `Bearer ${accessToken}` },
    });

    if (error) throw new Error(error.message || "Failed to confirm email");
    if (!data?.success) throw new Error(data?.error || "Failed to confirm email");
    return data.message;
  },

  async resendConfirmation(userId: string, accessToken: string): Promise<string> {
    const { data, error } = await supabase.functions.invoke("user-account", {
      body: { user_id: userId, action: "resend_confirmation" },
      headers: { Authorization: `Bearer ${accessToken}` },
    });

    if (error) throw new Error(error.message || "Failed to resend confirmation");
    if (!data?.success) throw new Error(data?.error || "Failed to resend confirmation");
    return data.message;
  },

  async setPassword(userId: string, password: string, accessToken: string): Promise<string> {
    const { data, error } = await supabase.functions.invoke("user-account", {
      body: { user_id: userId, action: "set_password", password },
      headers: { Authorization: `Bearer ${accessToken}` },
    });

    if (error) throw new Error(error.message || "Failed to set password");
    if (!data?.success) throw new Error(data?.error || "Failed to set password");
    return data.message;
  },

  async updateUser(id: string, patch: Partial<UserType>) {
    const { data, error } = await supabase.from("users").update(patch).eq("id", id).single();
    if (error) throw new Error(error.message);
    return data;
  },

  async saveRowPatches(patches: (UserType & { status: string; markets?: string[] })[]) {
    await Promise.all(
      patches.map(async (patch) => {
        // Deep merge meta_data if it contains nested updates
        let finalPatch: any = { ...patch };

        // Remove the virtual 'markets' field that was added for display
        delete finalPatch.markets;

        if (patch.meta_data) {
          // Fetch current meta_data to merge
          const { data: current } = await supabase.from("users").select("meta_data").eq("id", patch.id).single();

          // Deep merge the meta_data
          finalPatch.meta_data = {
            ...(current?.meta_data || {}),
            ...patch.meta_data,
          };
        }

        const { error } = await supabase.from("users").update(finalPatch).eq("id", patch.id);
        if (error) throw new Error(error.message);
      }),
    );
  },

  async deleteUser(id: string): Promise<boolean> {
    const { error } = await supabase.from("users").delete().eq("id", id);
    if (error) {
      throw new Error(error.message);
    }
    return true;
  },

  async deleteUsersById(userId: string): Promise<boolean> {
    const { error } = await supabase.from("users").delete().eq("id", userId);
    if (error) {
      throw new Error(error.message);
    }
    return true;
  },

  async deleteUsersByIds(userIds: string[]): Promise<boolean> {
    const { error } = await supabase.from("users").delete().in("id", userIds);
    if (error) {
      console.error("Error deleting Users by IDs:", error.message);
      return false;
    }
    return true;
  },

  // File management methods
  async uploadFile(userId: string, file: File, uploadedBy: string): Promise<FileMetadata> {
    // Get tenant_id from user record
    const { data: tenantData, error: tenantError } = await supabase
      .from("users")
      .select("tenant_id")
      .eq("id", userId)
      .single();

    if (tenantError || !tenantData) {
      throw new Error(`Failed to get user tenant: ${tenantError?.message || "User not found"}`);
    }

    const fileId = `${Date.now()}-${Math.random().toString(36).substr(2, 9)}`;
    const filePath = `${tenantData.tenant_id}/${userId}/${fileId}-${file.name}`;

    // Upload file to storage
    const { data: uploadData, error: uploadError } = await supabase.storage.from("users").upload(filePath, file);

    if (uploadError) {
      throw new Error(`Upload failed: ${uploadError.message}`);
    }

    // Create file metadata (store path, not URL)
    const fileMetadata: FileMetadata = {
      id: fileId,
      name: file.name,
      size: file.size,
      type: file.type,
      url: filePath, // Store path for signed URL generation
      bucket: "users", // Store bucket name
      uploadedAt: new Date().toISOString(),
      uploadedBy: uploadedBy,
    };

    // No database metadata tracking needed - files are listed directly from storage
    return fileMetadata;
  },

  async getFiles(userId: string): Promise<FileMetadata[]> {
    // Get tenant_id from user record
    const { data: tenantData, error: tenantError } = await supabase
      .from("users")
      .select("tenant_id")
      .eq("id", userId)
      .single();

    if (tenantError || !tenantData) {
      throw new Error(`Failed to get user tenant: ${tenantError?.message || "User not found"}`);
    }

    // List files from storage bucket
    const storagePath = `${tenantData.tenant_id}/${userId}`;
    const { data: files, error: listError } = await supabase.storage.from("users").list(storagePath);

    if (listError) {
      throw new Error(`Failed to list user files: ${listError.message}`);
    }

    if (!files || files.length === 0) {
      return [];
    }

    // Convert storage files to FileMetadata
    const fileMetadata: FileMetadata[] = files.map((file) => {
      // Parse fileId from filename (format: {fileId}-{filename})
      const dashIndex = file.name.indexOf("-");
      const fileId = dashIndex > 0 ? file.name.substring(0, dashIndex) : file.name;
      const fileName = dashIndex > 0 ? file.name.substring(dashIndex + 1) : file.name;

      return {
        id: fileId,
        name: fileName,
        size: file.metadata?.size || 0,
        type: file.metadata?.mimetype || "application/octet-stream",
        url: `${storagePath}/${file.name}`, // Store path for signed URL generation
        bucket: "users", // Store bucket name
        uploadedAt: file.created_at || new Date().toISOString(),
        uploadedBy: "unknown", // Storage doesn't track uploader, would need database for this
      };
    });

    return fileMetadata;
  },

  async deleteFile(userId: string, fileId: string): Promise<boolean> {
    // Get tenant_id from user record
    const { data: tenantData, error: tenantError } = await supabase
      .from("users")
      .select("tenant_id")
      .eq("id", userId)
      .single();

    if (tenantError || !tenantData) {
      throw new Error(`Failed to get user tenant: ${tenantError?.message || "User not found"}`);
    }

    // List files from storage to find the one to delete
    const storagePath = `${tenantData.tenant_id}/${userId}`;
    const { data: files, error: listError } = await supabase.storage.from("users").list(storagePath);

    if (listError) {
      throw new Error(`Failed to list user files: ${listError.message}`);
    }

    // Find the file that starts with the fileId
    const fileToDelete = files?.find((file) => file.name.startsWith(`${fileId}-`));

    if (!fileToDelete) {
      throw new Error("File not found in storage");
    }

    // Delete from storage
    const filePath = `${storagePath}/${fileToDelete.name}`;
    const { error: storageError } = await supabase.storage.from("users").remove([filePath]);

    if (storageError) {
      throw new Error(`Failed to delete from storage: ${storageError.message}`);
    }

    return true;
  },

  async renameFile(userId: string, fileId: string, newName: string): Promise<FileMetadata> {
    const { data: tenantData, error: tenantError } = await supabase
      .from("users")
      .select("tenant_id")
      .eq("id", userId)
      .single();

    if (tenantError || !tenantData) {
      throw new Error(`Failed to get user tenant: ${tenantError?.message || "User not found"}`);
    }

    const storagePath = `${tenantData.tenant_id}/${userId}`;
    const { data: files, error: listError } = await supabase.storage.from("users").list(storagePath);

    if (listError) {
      throw new Error(`Failed to list user files: ${listError.message}`);
    }

    const fileToRename = files?.find((file) => file.name.startsWith(`${fileId}-`));

    if (!fileToRename) {
      throw new Error("File not found in storage");
    }

    const oldPath = `${storagePath}/${fileToRename.name}`;
    const newPath = `${storagePath}/${fileId}-${newName}`;

    const { error: moveError } = await supabase.storage.from("users").move(oldPath, newPath);

    if (moveError) {
      throw new Error(`Failed to rename file: ${moveError.message}`);
    }

    return {
      id: fileId,
      name: newName,
      size: fileToRename.metadata?.size || 0,
      type: fileToRename.metadata?.mimetype || "application/octet-stream",
      url: newPath,
      bucket: "users",
      uploadedAt: fileToRename.created_at || new Date().toISOString(),
      uploadedBy: "unknown",
    };
  },

  // Avatar management methods
  async uploadAvatar(userId: string, file: File): Promise<string> {
    // Get tenant_id from user record
    const { data: userData, error: userError } = await supabase
      .from("users")
      .select("tenant_id")
      .eq("id", userId)
      .single();

    if (userError || !userData) {
      throw new Error(`Failed to get user tenant: ${userError?.message || "User not found"}`);
    }

    // Get file extension
    const extension = file.name.split(".").pop()?.toLowerCase() || "jpg";
    const filePath = `${userData.tenant_id}/users/${userId}.${extension}`;

    // Delete any existing avatar first
    try {
      await this.deleteAvatar(userId);
    } catch (error) {
      // Ignore error if no existing avatar
      console.log("No existing avatar to delete or delete failed:", error);
    }

    // Upload new avatar to storage
    const { error: uploadError } = await supabase.storage.from("avatars").upload(filePath, file, {
      upsert: true,
    });

    if (uploadError) {
      throw new Error(`Avatar upload failed: ${uploadError.message}`);
    }

    // Get public URL
    const { data: urlData } = supabase.storage.from("avatars").getPublicUrl(filePath);

    // Clear the avatar cache so Profile component refetches
    clearAvatarCache(userId);

    return urlData.publicUrl;
  },

  async deleteAvatar(userId: string): Promise<boolean> {
    // Get tenant_id from user record
    const { data: userData, error: userError } = await supabase
      .from("users")
      .select("tenant_id")
      .eq("id", userId)
      .single();

    if (userError || !userData) {
      throw new Error(`Failed to get user tenant: ${userError?.message || "User not found"}`);
    }

    // Try to delete all possible avatar file extensions
    const extensions = ["jpg", "jpeg", "png", "gif", "webp"];
    const filePaths = extensions.map((ext) => `${userData.tenant_id}/users/${userId}.${ext}`);

    // Also check for files without extension
    filePaths.push(`${userData.tenant_id}/users/${userId}`);

    const { error } = await supabase.storage.from("avatars").remove(filePaths);

    if (error) {
      throw new Error(`Failed to delete avatar: ${error.message}`);
    }

    // Clear the avatar cache so Profile component refetches
    clearAvatarCache(userId);

    return true;
  },

  // Link table management methods
  async syncUserPositions(userId: string, positions: { position: { id: string }; meta_data?: any }[]): Promise<void> {
    // Delete all existing user positions
    const { error: deleteError } = await supabase.schema("link").from("user_positions").delete().eq("id", userId);

    if (deleteError) {
      throw new Error(`Failed to delete existing user positions: ${deleteError.message}`);
    }

    // Insert new positions
    if (positions.length > 0) {
      const records = positions.map((p) => ({
        id: userId,
        position_id: p.position.id,
        meta_data: p.meta_data || {},
      }));

      const { error: insertError } = await supabase.schema("link").from("user_positions").insert(records);

      if (insertError) {
        throw new Error(`Failed to insert user positions: ${insertError.message}`);
      }
    }
  },

  async syncUserAbilities(
    userId: string,
    abilities: { ability: { id: string }; expires?: string | null }[],
  ): Promise<void> {
    // Delete all existing user abilities
    const { error: deleteError } = await supabase.schema("link").from("user_abilities").delete().eq("id", userId);

    if (deleteError) {
      throw new Error(`Failed to delete existing user abilities: ${deleteError.message}`);
    }

    // Insert new abilities
    if (abilities.length > 0) {
      const records = abilities.map((a) => ({
        id: userId,
        ability_id: a.ability.id,
        expires: a.expires || null,
      }));

      const { error: insertError } = await supabase.schema("link").from("user_abilities").insert(records);

      if (insertError) {
        throw new Error(`Failed to insert user abilities: ${insertError.message}`);
      }
    }
  },

  async syncUserRestrictions(
    userId: string,
    restrictions: { restriction: { id: string }; expires?: string | null }[],
  ): Promise<void> {
    // Delete all existing user restrictions
    const { error: deleteError } = await supabase.schema("link").from("user_restrictions").delete().eq("id", userId);

    if (deleteError) {
      throw new Error(`Failed to delete existing user restrictions: ${deleteError.message}`);
    }

    // Insert new restrictions
    if (restrictions.length > 0) {
      const records = restrictions.map((r) => ({
        id: userId,
        restriction_id: r.restriction.id,
        expires: r.expires || null,
      }));

      const { error: insertError } = await supabase.schema("link").from("user_restrictions").insert(records);

      if (insertError) {
        throw new Error(`Failed to insert user restrictions: ${insertError.message}`);
      }
    }
  },

  async syncUserRoles(userId: string, roleIds: string[]): Promise<void> {
    console.log("[syncUserRoles] Called with:", { userId, roleIds });

    // Delete all existing user roles
    const { error: deleteError } = await supabase.schema("link").from("roles_users").delete().eq("user_id", userId);

    if (deleteError) {
      console.error("[syncUserRoles] Delete error:", deleteError);
      throw new Error(`Failed to delete existing user roles: ${deleteError.message}`);
    }
    console.log("[syncUserRoles] Deleted existing roles");

    // Insert new roles (tenant_id comes from RLS via auth.jwt()->>'tenant_id')
    if (roleIds.length > 0) {
      const records = roleIds.map((roleId) => ({
        user_id: userId,
        role_id: roleId,
      }));
      console.log("[syncUserRoles] Inserting records:", records);

      const { error: insertError, data: insertData } = await supabase
        .schema("link")
        .from("roles_users")
        .insert(records)
        .select();

      console.log("[syncUserRoles] Insert result:", { insertData, insertError });

      if (insertError) {
        console.error("[syncUserRoles] Insert error:", insertError);
        throw new Error(`Failed to insert user roles: ${insertError.message}`);
      }
    } else {
      console.log("[syncUserRoles] No roleIds to insert");
    }
    console.log("[syncUserRoles] Completed successfully");
  },

  async updateUserPrivate(userId: string, privateData: any): Promise<void> {
    // Upsert user_private record
    const { error } = await supabase
      .from("user_private")
      .upsert(
        {
          id: userId,
          ...privateData,
        },
        { onConflict: "id,tenant_id" },
      )
      .select()
      .single();

    if (error) {
      throw new Error(`Failed to update user private data: ${error.message}`);
    }
  },

  async updateUserPay(userId: string, payData: any): Promise<void> {
    // Upsert user_pay record
    const { error } = await supabase
      .from("user_pay")
      .upsert(
        {
          id: userId,
          ...payData,
        },
        { onConflict: "id,tenant_id" },
      )
      .select()
      .single();

    if (error) {
      throw new Error(`Failed to update user pay data: ${error.message}`);
    }
  },

  async syncUserEmergencyContacts(userId: string, contacts: Partial<EmergencyContactType>[]): Promise<void> {
    // Get existing contacts for this user
    const { data: existing, error: fetchError } = await supabase
      .from("user_emergency_contacts")
      .select("id")
      .eq("user_id", userId);

    if (fetchError) {
      throw new Error(`Failed to fetch existing emergency contacts: ${fetchError.message}`);
    }

    const existingIds = new Set(existing?.map((c) => c.id) || []);
    const newIds = new Set(contacts.filter((c) => c.id).map((c) => c.id));

    // Delete removed contacts
    const toDelete = [...existingIds].filter((id) => !newIds.has(id));
    if (toDelete.length > 0) {
      const { error: deleteError } = await supabase.from("user_emergency_contacts").delete().in("id", toDelete);

      if (deleteError) {
        throw new Error(`Failed to delete emergency contacts: ${deleteError.message}`);
      }
    }

    // Upsert contacts (insert or update)
    for (const contact of contacts) {
      if (contact.id && existingIds.has(contact.id)) {
        // Update existing
        const { error: updateError } = await supabase
          .from("user_emergency_contacts")
          .update({
            first_name: contact.first_name,
            last_name: contact.last_name,
            relationship: contact.relationship,
            phones: contact.phones,
            emails: contact.emails,
          })
          .eq("id", contact.id);

        if (updateError) {
          throw new Error(`Failed to update emergency contact: ${updateError.message}`);
        }
      } else {
        // Insert new
        const { error: insertError } = await supabase.from("user_emergency_contacts").insert({
          user_id: userId,
          first_name: contact.first_name,
          last_name: contact.last_name,
          relationship: contact.relationship,
          phones: contact.phones || [],
          emails: contact.emails || [],
        });

        if (insertError) {
          throw new Error(`Failed to insert emergency contact: ${insertError.message}`);
        }
      }
    }
  },

  // User Reviews CRUD
  async getReviewsByUserId(userId: string) {
    const { data, error } = await supabase
      .from("user_reviews")
      .select(
        `
        *,
        created_by_user:users!user_reviews_created_by_tenant_id_fkey(id, first_name, last_name),
        submitter:users!user_reviews_tenant_id_submitted_by_fkey(id, first_name, last_name)
      `,
      )
      .eq("user_id", userId)
      .order("created_at", { ascending: false });

    if (error) {
      throw new Error(`Failed to fetch reviews: ${error.message}`);
    }

    return data || [];
  },

  async createReview(review: {
    user_id: string;
    rating: number;
    message?: string;
    submitted_by?: string;
    meta_data?: { category?: string; period?: string };
  }) {
    const { data, error } = await supabase
      .from("user_reviews")
      .insert({
        user_id: review.user_id,
        rating: review.rating,
        message: review.message,
        submitted_by: review.submitted_by,
        meta_data: review.meta_data || {},
      })
      .select(
        `
        *,
        created_by_user:users!user_reviews_created_by_tenant_id_fkey(id, first_name, last_name),
        submitter:users!user_reviews_tenant_id_submitted_by_fkey(id, first_name, last_name)
      `,
      )
      .single();

    if (error) {
      throw new Error(`Failed to create review: ${error.message}`);
    }

    return data;
  },

  async updateReview(
    reviewId: string,
    updates: {
      rating?: number;
      message?: string;
      submitted_by?: string;
      meta_data?: { category?: string; period?: string };
    },
  ) {
    const { data, error } = await supabase
      .from("user_reviews")
      .update(updates)
      .eq("id", reviewId)
      .select(
        `
        *,
        created_by_user:users!user_reviews_created_by_tenant_id_fkey(id, first_name, last_name),
        submitter:users!user_reviews_tenant_id_submitted_by_fkey(id, first_name, last_name)
      `,
      )
      .single();

    if (error) {
      throw new Error(`Failed to update review: ${error.message}`);
    }

    return data;
  },

  async deleteReview(reviewId: string) {
    const { error } = await supabase.from("user_reviews").delete().eq("id", reviewId);

    if (error) {
      throw new Error(`Failed to delete review: ${error.message}`);
    }
  },

  async getUsersByCellPhones(numbers: string[]) {
    const last10 = Array.from(
      new Set(
        numbers
          .map((n) => n.replace(/\D/g, ""))
          .map((d) => d.slice(-10))
          .filter((d) => d.length === 10),
      ),
    );
    if (last10.length === 0) return [];
    const { data, error } = await supabase.rpc("find_users_by_cell_phone", { p_last10: last10 });
    if (error) {
      console.error("[userService] getUsersByCellPhones failed:", error.message);
      return [];
    }
    return (data ?? []) as {
      id: string;
      first_name: string | null;
      last_name: string | null;
      preferred_name: string | null;
      last10: string;
    }[];
  },
};
